import React from 'react';
import clsx from 'clsx';

interface AvatarProps {
  src?: string | null;
  name?: string;
  email?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeClasses = {
  xs: 'w-6 h-6 text-xs',
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base',
  xl: 'w-16 h-16 text-lg',
};

const getInitials = (name?: string, email?: string): string => {
  if (name && name.trim()) {
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  
  if (email) {
    const [username] = email.split('@');
    return username
      .split(/[.\-_]/)
      .map(part => part[0]?.toUpperCase())
      .join('')
      .slice(0, 2);
  }
  
  return 'U';
};

export default function Avatar({ src, name, email, size = 'md', className = '' }: AvatarProps) {
  const [imageError, setImageError] = React.useState(false);
  const [imageLoaded, setImageLoaded] = React.useState(false);

  React.useEffect(() => {
    setImageError(false);
    setImageLoaded(false);
  }, [src]);

  // Keep initials visible until remote avatars finish loading.
  const showFallback = !src || imageError || !imageLoaded;
  const initials = getInitials(name, email);

  return (
    <div
      className={clsx(
        'rounded-full flex items-center justify-center font-bold flex-shrink-0 overflow-hidden',
        sizeClasses[size],
        showFallback && 'bg-gradient-to-br from-indigo-600 to-purple-700 text-white shadow-sm',
        className
      )}
      title={name || email || 'User'}
    >
      {src && !imageError && (
        <img
          src={src}
          alt={name || email || 'User avatar'}
          className={clsx(
            'w-full h-full object-cover transition-opacity duration-200',
            imageLoaded ? 'opacity-100' : 'opacity-0'
          )}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
        />
      )}
      {showFallback && (
        <span className="select-none">{initials}</span>
      )}
    </div>
  );
}

